/**
 * Lista del manifiesto activo por transportadora.
 */
import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { Button } from '../../components/ui'
import DataTable from '../../components/ui/DataTable'
import { generarPDFManifiesto } from '../../components/pdf-templates/ManifiestoPDF'
import {
  MANIFIESTOS_CARRIERS,
  carrierLabel,
} from '../../config/manifiestos/manifiestosCarriersConfig'
import { usePermissions } from '../../hooks/usePermissions'
import {
  ManifiestoApiError,
  crearManifiesto,
  getManifiesto,
  getManifiestoActivo,
  getPedidoDetalle,
} from '../../services/manifiestos/manifiestosService'
import {
  MANIFIESTO_ESTADO_IMPRESO,
  MANIFIESTO_ESTADO_LISTO,
  MANIFIESTO_LINEA_ACTIVA,
  type Manifiesto,
  type ManifiestoPedidoLinea,
} from '../../types/manifiestos'
import { mapOrdersToManifiestoRows } from '../../utils/manifiestos/manifiestoMapper'

function lineasActivas(manifiesto: Manifiesto | null): ManifiestoPedidoLinea[] {
  const lineas = manifiesto?.pedidos || []
  return lineas.filter((linea) => {
    const estado = String(linea.estado || MANIFIESTO_LINEA_ACTIVA)
    return estado === MANIFIESTO_LINEA_ACTIVA
  })
}

const Manifiestos: React.FC = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { hasPermission } = usePermissions()
  const canManage = hasPermission('manage_manifiestos')
  const [transportadora, setTransportadora] = useState('')
  const [manifiesto, setManifiesto] = useState<Manifiesto | null>(null)
  const [caso, setCaso] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [working, setWorking] = useState(false)

  const aplicarManifiesto = useCallback((next: Manifiesto | null, nextCaso: string) => {
    setManifiesto(next)
    setCaso(nextCaso)
  }, [])

  useEffect(() => {
    if (!id) return
    const manifiestoId = Number(id)
    if (!Number.isInteger(manifiestoId)) return
    setLoading(true)
    void getManifiesto(manifiestoId)
      .then((full) => {
        setTransportadora(full.transportadora || '')
        aplicarManifiesto(full, 'abierto')
      })
      .catch((error: unknown) => {
        const code = error instanceof ManifiestoApiError ? error.code : ''
        const message = code === 'MANIFIESTO_NO_ENCONTRADO'
          ? 'No se encontró el manifiesto'
          : error instanceof Error ? error.message : 'No se pudo abrir el manifiesto'
        toast.error(message, { position: 'top-center' })
      })
      .finally(() => setLoading(false))
  }, [id, aplicarManifiesto])

  const cargarActivo = useCallback(async (value: string) => {
    setTransportadora(value)
    if (!value) {
      aplicarManifiesto(null, '')
      return
    }
    setLoading(true)
    try {
      const data = await getManifiestoActivo(value)
      if (data.caso === 'abierto' && data.manifiesto?.id) {
        const full = await getManifiesto(data.manifiesto.id)
        aplicarManifiesto(
          { ...data.manifiesto, ...full, id: data.manifiesto.id },
          'abierto'
        )
      } else {
        aplicarManifiesto(null, data.caso)
      }
    } catch (error) {
      aplicarManifiesto(null, '')
      const message = error instanceof Error ? error.message : 'No se pudo consultar el manifiesto'
      toast.error(message, { position: 'top-center' })
    } finally {
      setLoading(false)
    }
  }, [aplicarManifiesto])

  const handleCrear = async () => {
    if (!transportadora) return
    setWorking(true)
    try {
      const creado = await crearManifiesto(transportadora)
      const full = await getManifiesto(creado.id)
      aplicarManifiesto({ ...creado, ...full, estado: full.estado || 'Creado' }, 'abierto')
      toast.success('Manifiesto creado', { position: 'top-center' })
    } catch (error) {
      if (
        error instanceof ManifiestoApiError &&
        error.status === 409 &&
        error.code === 'MANIFIESTO_ABIERTO'
      ) {
        toast('Ya hay un manifiesto abierto para esta transportadora', { position: 'top-center' })
        await cargarActivo(transportadora)
        return
      }
      const message = error instanceof Error ? error.message : 'No se pudo crear el manifiesto'
      toast.error(message, { position: 'top-center' })
    } finally {
      setWorking(false)
    }
  }

  const handleImprimir = async () => {
    if (!manifiesto?.id) return
    setWorking(true)
    try {
      const full = await getManifiesto(manifiesto.id)
      const activas = lineasActivas(full)
      if (activas.length === 0) {
        toast.error('No hay pedidos activos para imprimir', { position: 'top-center' })
        return
      }
      const detalles: unknown[] = []
      for (const linea of activas) {
        const detalle = await getPedidoDetalle(linea.id_pedido)
        const base = detalle && typeof detalle === 'object' ? detalle as Record<string, unknown> : {}
        detalles.push({
          ...base,
          id: linea.id_pedido,
          guia: linea.guia || base.guia || '—',
          _numero: linea.numero_pedido,
          _paq: linea.paquetes_verificados,
          _und: linea.unidades_empaque,
        })
      }
      const { rows } = mapOrdersToManifiestoRows(detalles)
      rows.forEach((row) => {
        const source = detalles.find((item) => {
          const record = item as { id?: number }
          return Number(record.id) === row.orderId
        }) as { _numero?: string | number; _paq?: number; _und?: number; guia?: string } | undefined
        if (!source) return
        row.numeroVisible = String(source._numero ?? row.orderId)
        if (source.guia) row.guia = String(source.guia)
        const unidades = Number(source._und)
        const paquetes = Number(source._paq)
        if (Number.isFinite(unidades) && unidades > 0) row.und = unidades
        else if (Number.isFinite(paquetes) && paquetes > 0) row.und = paquetes
        if (Number.isFinite(paquetes) && paquetes > 0) {
          const cajas = `${paquetes} CAJ`
          row.contenido = row.contenido ? `${cajas} · ${row.contenido}` : cajas
        }
      })
      const option = MANIFIESTOS_CARRIERS.find((item) => item.transportadora === transportadora)
      await generarPDFManifiesto(
        option || { id: 'manifiesto', label: carrierLabel(transportadora), transportadora },
        rows
      )
      toast.success('Manifiesto listo para imprimir', { position: 'top-center' })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'No se pudo armar el PDF'
      toast.error(message, { position: 'top-center' })
    } finally {
      setWorking(false)
    }
  }

  const creadoPor = manifiesto?.creadoPor
    ? `${manifiesto.creadoPor.nombre} ${manifiesto.creadoPor.apellido}`.trim()
    : ''
  const verificadoPor = manifiesto?.verificadoPor
    ? `${manifiesto.verificadoPor.nombre} ${manifiesto.verificadoPor.apellido}`.trim()
    : ''
  const estado = manifiesto?.estado || ''
  const puedeImprimir = estado === MANIFIESTO_ESTADO_LISTO || estado === MANIFIESTO_ESTADO_IMPRESO
  const puedeCrear = canManage && (caso === 'sin_manifiesto' || caso === 'cerrado') && !manifiesto
  const filas = (manifiesto?.pedidos || []).map((linea) => ({
    ...linea,
    numero: String(linea.numero_pedido ?? ''),
    estadoLinea: linea.estado || '',
    guiaLinea: linea.guia || '',
    escaneados: linea.paquetes_verificados ?? 0,
    cajas: linea.unidades_empaque ?? 0,
  }))

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      <div className="flex flex-col gap-4 mb-6">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-xl font-semibold">{id ? 'Detalle del manifiesto' : 'Crear manifiesto'}</h2>
            <button type="button" className="text-sm text-blue-700" onClick={() => navigate('/envios/manifiestos')}>
              Ver manifiestos
            </button>
          </div>
        <div className="flex flex-col md:flex-row md:items-end gap-3">
          {!id && (
            <label className="flex flex-col gap-1 text-sm text-gray-600 min-w-[240px]">
              Transportadora
              <select
                className="border border-gray-300 rounded-md px-3 py-2 text-gray-900"
                value={transportadora}
                onChange={(event) => void cargarActivo(event.target.value)}
                aria-label="Transportadora del manifiesto"
              >
                <option value="">Selecciona una transportadora</option>
                {MANIFIESTOS_CARRIERS.map((item) => (
                  <option key={item.id} value={item.transportadora}>{item.label}</option>
                ))}
              </select>
            </label>
          )}
          {id && manifiesto && (
            <div className="text-sm text-gray-600 space-y-1">
              <p>Transportadora: <span className="font-medium text-gray-900">{carrierLabel(manifiesto.transportadora) || manifiesto.transportadora}</span></p>
              {manifiesto.fechaCreacion && <p>Creado: {manifiesto.fechaCreacion}</p>}
              {creadoPor && <p>Creado por: {creadoPor}</p>}
              {verificadoPor && <p>Verificado por: {verificadoPor}</p>}
            </div>
          )}
          {estado && (
            <p className="text-sm text-gray-700">
              Manifiesto {manifiesto?.id} · estado <span className="font-semibold">{estado}</span>
            </p>
          )}
          {!manifiesto && caso && (
            <p className="text-sm text-gray-500">
              {caso === 'cerrado' ? 'El último manifiesto está cerrado.' : 'No hay manifiesto abierto.'}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {puedeCrear && (
            <Button variant="primary" isLoading={working} onClick={() => void handleCrear()}>
              Crear
            </Button>
          )}
          {puedeImprimir && (
            <Button variant="primary" isLoading={working} onClick={() => void handleImprimir()}>
              Imprimir
            </Button>
          )}
        </div>
      </div>

      <DataTable
        data={filas}
        isLoading={loading}
        emptyMessage={transportadora ? 'Este manifiesto no tiene pedidos' : 'Elige una transportadora'}
        getRowId={(row) => row.id_pedido}
        columns={[
          { header: 'Pedido', accessor: 'numero', sortable: true },
          { header: 'Estado', accessor: 'estadoLinea', sortable: true },
          { header: 'Guía', accessor: 'guiaLinea' },
          { header: 'Escaneados', accessor: 'escaneados' },
          { header: 'Cajas', accessor: 'cajas' },
        ]}
      />
    </div>
  )
}

export default Manifiestos
